import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { policies, categories } from "@/data/policyRegistry";
import { Reveal } from "@/components/site/Reveal";
import useSeo from "@/hooks/useSeo";

export default function PoliciesIndex() {
  useSeo({ title: "Orrbbit Legal & Safety Centre", description: "Read Orrbbit's policies covering privacy, safety, People, Business and current launch access.", path: "/policies" });

  return (
    <div data-testid="policies-index-page">
      <section className="border-b border-border bg-lightbg">
        <div className="container-x section-space">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-teal">Trust &amp; Legal</span>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">Orrbbit Legal &amp; Safety Centre</h1>
            <p className="mt-4 max-w-2xl text-lg text-bodygrey">Everything that governs how Orrbbit operates and how we keep our community safe. Each policy shows its own version, effective date and last-updated date.</p>
          </Reveal>
        </div>
      </section>

      <div className="container-x py-14">
        {categories.map((cat) => {
          const items = policies.filter((p) => p.category === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat} className="mb-14">
              <h2 className="font-heading text-xl font-semibold text-navy md:text-2xl">{cat}</h2>
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {cat === "Safety & Community" && (
                  <Reveal>
                    <Link to="/report" data-testid="policy-card-report"
                      className="group flex h-full flex-col rounded-2xl border border-orange/30 bg-orange/5 p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-card">
                      <div className="flex items-start justify-between">
                        <span className="text-xs font-semibold text-orange">Online tool</span>
                        <ArrowUpRight size={18} className="text-bodygrey transition-all group-hover:-translate-y-0.5 group-hover:text-orange" />
                      </div>
                      <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-navy group-hover:text-orange">Report a Concern</h3>
                      <p className="mt-2 flex-1 text-sm text-bodygrey">Report a safety concern, harassment, fake profile, scam, event issue or anything else that shouldn't be on Orrbbit.</p>
                      <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-bodygrey">
                        <span>Reviewed by our team</span>
                        <span className="font-semibold text-orange">Report →</span>
                      </div>
                    </Link>
                  </Reveal>
                )}
                {cat === "Safety & Community" && (
                  <Reveal delay={0.04}>
                    <Link to="/delete-account" data-testid="policy-card-delete-account"
                      className="group flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-card">
                      <div className="flex items-start justify-between">
                        <span className="text-xs font-semibold text-teal">Online tool</span>
                        <ArrowUpRight size={18} className="text-bodygrey transition-all group-hover:-translate-y-0.5 group-hover:text-teal" />
                      </div>
                      <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-navy group-hover:text-teal">Delete Your Account</h3>
                      <p className="mt-2 flex-1 text-sm text-bodygrey">Request account deletion online — in the app or here on the website, confirmed by a secure email link.</p>
                      <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-bodygrey">
                        <span>Secure email confirmation</span>
                        <span className="font-semibold text-teal">Start →</span>
                      </div>
                    </Link>
                  </Reveal>
                )}
                {items.map((p, i) => (
                  <Reveal key={p.slug} delay={i * 0.04}>
                    <Link to={p.route} data-testid={`policy-card-${p.slug}`}
                      className="group flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-card">
                      <div className="flex items-start justify-between">
                        <span className="text-xs font-semibold text-teal">Version {p.version}</span>
                        <ArrowUpRight size={18} className="text-bodygrey transition-all group-hover:-translate-y-0.5 group-hover:text-teal" />
                      </div>
                      <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-navy group-hover:text-teal">{p.title}</h3>
                      <p className="mt-2 flex-1 text-sm text-bodygrey">{p.description}</p>
                      <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-bodygrey">
                        <span>Effective {p.effective_date}</span>
                        <span className="font-semibold text-teal">View Policy →</span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

